// Onboarding page for a signed-in member who isn't linked to any product yet.
// They can create a product (becoming its owner) and are then sent back to the
// setup page, where the new product is now selectable.
import {
  createProduct,
  me,
  getToken,
  clearToken,
  captureTokenFromHash,
} from "./api.js";
import { el, describeError } from "./dom.js";
import { clearSession } from "./state.js";

const registerForm = el<HTMLFormElement>("register-form");
const nameInput = el<HTMLInputElement>("product-name");
const descInput = el<HTMLTextAreaElement>("product-description");
const createBtn = el<HTMLButtonElement>("create-product");
const status = el<HTMLParagraphElement>("register-status");
const signedIn = el<HTMLElement>("signed-in");
const signOutBtn = el<HTMLButtonElement>("sign-out");
const emptyState = el<HTMLDivElement>("empty-state");
const createHeader = el<HTMLDivElement>("create-header");
const noAccess = el<HTMLDivElement>("no-access");
const noAccessTitle = el<HTMLHeadingElement>("no-access-title");
const noAccessDetail = el<HTMLParagraphElement>("no-access-detail");

function setStatus(text: string, isError = false): void {
  status.textContent = text;
  status.classList.toggle("error", isError);
}

registerForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  const name = nameInput.value.trim();
  if (!name) {
    setStatus("Enter a product name.", true);
    return;
  }
  createBtn.disabled = true;
  setStatus("Creating product…");
  try {
    await createProduct(name, descInput.value.trim() || null);
    // The product now shows up under /me, so hand off to the setup page to open
    // a session against it.
    location.href = "index.html";
  } catch (err) {
    setStatus(describeError(err), true);
    createBtn.disabled = false;
  }
});

signOutBtn.addEventListener("click", () => {
  clearToken();
  clearSession();
  location.replace("index.html");
});

// Gate the page: must be signed in, and only relevant when you have no products.
async function init(): Promise<void> {
  captureTokenFromHash();
  if (!getToken()) {
    location.replace("index.html");
    return;
  }
  try {
    const profile = await me();
    signedIn.textContent = `Signed in as ${profile.email}`;
    const isEmpty = profile.products.length === 0;

    // Creating a product is granted by hand and off by default, so signing in
    // says nothing about being able to. Show why they are here instead of a form
    // that would only fail on submit.
    if (!profile.can_create_products) {
      registerForm.hidden = true;
      noAccess.hidden = false;
      if (isEmpty) {
        noAccessTitle.textContent = "No products yet";
        noAccessDetail.textContent =
          "Your account isn't linked to a product. Ask a product owner to invite you, " +
          "or open Doc-Support from BinDist to have one set up.";
      } else {
        noAccessTitle.textContent = "Product creation isn't enabled";
        noAccessDetail.textContent =
          "Your account can't create products. Ask an administrator if you need to.";
      }
      return;
    }

    // "No products yet" only when the account is genuinely empty; otherwise this
    // is just creating another product.
    emptyState.hidden = !isEmpty;
    createHeader.hidden = isEmpty;
  } catch {
    clearToken();
    location.replace("index.html");
  }
}
void init();
